<template>
  <div>
    <h4 class="table-page-header">
      Time Off Requests
      <el-tooltip content="Request Time Off">
        <el-button type="text" @click="openCreateModal">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>

    <!-- Filters -->
    <div class="compact-filter-section">
      <div class="filter-row-compact">
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedStatus"
            :options="statusOptions"
            placeholder="Status"
            @input="handleFilterChange"
            class="compact-select"
          />
        </div>
        <div class="filter-group-compact date-range-group">
          <el-date-picker
            v-model="selectedDateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :clearable="true"
            @change="handleFilterChange"
            class="compact-date-picker"
          />
        </div>
        <div v-if="isAdmin" class="filter-group-compact">
          <AIVSelect
            v-model="selectedCaregiver"
            :options="caregiverOptions"
            placeholder="Caregiver"
            :multiSelect="true"
            :searchable="true"
            :clearable="true"
            @input="handleFilterChange"
            class="compact-select"
          />
        </div>
        <div v-if="isAdmin" class="filter-group-compact">
          <AIVSelect
            v-model="selectedAdmin"
            :options="adminOptions"
            placeholder="Manager"
            :multiSelect="true"
            :searchable="true"
            :clearable="true"
            @input="handleFilterChange"
            class="compact-select"
          />
        </div>
      </div>
    </div>

    <!-- Table -->
    <AIVTable
      :columns="tableColumns"
      :data="requests"
      :loading="loading"
      :pagination="pagination"
      :item-name="'requests'"
      @pageChange="loadRequests"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
    >
      <template #cell-employee="{ row }">
        <span v-if="row.caregiver">
          {{ row.caregiver.first_name }} {{ row.caregiver.last_name }}
        </span>
      </template>

      <template #cell-manager="{ row }">
        <span v-if="row.caregiver && row.caregiver.admin">
          {{ row.caregiver.admin.first_name }} {{ row.caregiver.admin.last_name }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell-start="{ row }">
        <div>
          {{ formatDate(row.start_date) }}
          <div v-if="row.request_type === 'single_partial_day'" class="text-muted small">
            {{ formatTimeRange(row.start_time, row.end_time) }}
          </div>
        </div>
      </template>

      <template #cell-end="{ row }">
        <span v-if="row.request_type === 'multiple_full_days'">
          {{ formatDate(row.end_date) }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell-duration="{ row }">
        <span v-if="row.request_type === 'single_partial_day'">
          Partial
        </span>
        <span v-else>
          {{ calculateDuration(row) }} day{{ calculateDuration(row) > 1 ? 's' : '' }}
        </span>
      </template>

      <template #cell-status="{ row }">
        <el-tag
          :type="getStatusType(row.status)"
          size="small"
        >
          {{ capitalizeStatus(row.status) }}
        </el-tag>
      </template>

      <template #cell-reviewed_by="{ row }">
        <span v-if="row.reviewer">
          {{ row.reviewer.first_name }} {{ row.reviewer.last_name }}
          <div class="text-muted small">
            {{ formatDateTime(row.reviewed_at) }}
          </div>
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell-actions="{ row }">
        <!-- Review button (Admin only, pending requests, not own requests) -->
        <button
          v-if="canReview(row)"
          class="btn btn-sm btn-primary mr-2"
          @click.stop="openReviewModal(row)"
          title="Review Request"
        >
          <i class="fa-solid fa-check"></i>
        </button>

        <!-- View button -->
        <button
          class="btn btn-sm btn-info mr-2"
          @click.stop="viewRequest(row)"
          title="View Details"
        >
          <i class="fa-solid fa-eye"></i>
        </button>

        <!-- Edit button (only for own pending requests) -->
        <button
          v-if="canEdit(row)"
          class="btn btn-sm btn-warning mr-2"
          @click.stop="editRequest(row)"
          title="Edit Request"
        >
          <i class="fa-solid fa-pen-to-square"></i>
        </button>

        <!-- Delete button (only for own pending requests) -->
        <button
          v-if="canDelete(row)"
          class="btn btn-sm btn-danger"
          @click.stop="deleteRequest(row)"
          title="Delete Request"
        >
          <i class="fa-solid fa-trash"></i>
        </button>
      </template>
    </AIVTable>

    <!-- Modals -->
    <TimeOffRequestModal
      v-model="showRequestModal"
      :editData="selectedRequest"
      @saved="handleRequestSaved"
    />

    <TimeOffReviewModal
      v-model="showReviewModal"
      :request="selectedRequest"
      @reviewed="handleRequestReviewed"
    />

    <!-- View Details Modal -->
    <AIVDialog
      v-model="showViewModal"
      title="Time Off Request Details"
      width="500px"
    >
      <div v-if="selectedRequest" class="view-details-content">
        <div class="detail-row">
          <strong>Employee:</strong>
          <span v-if="selectedRequest.caregiver">
            {{ selectedRequest.caregiver.first_name }} {{ selectedRequest.caregiver.last_name }}
          </span>
        </div>
        <div class="detail-row">
          <strong>Type:</strong>
          <span>{{ formatRequestType(selectedRequest.request_type) }}</span>
        </div>
        <div class="detail-row">
          <strong>{{ selectedRequest.request_type === 'multiple_full_days' ? 'Start Date:' : 'Date:' }}</strong>
          <span>{{ formatDate(selectedRequest.start_date) }}</span>
        </div>
        <div class="detail-row" v-if="selectedRequest.request_type === 'multiple_full_days'">
          <strong>End Date:</strong>
          <span>{{ formatDate(selectedRequest.end_date) }}</span>
        </div>
        <div class="detail-row" v-if="selectedRequest.request_type === 'single_partial_day'">
          <strong>Time:</strong>
          <span>{{ formatTimeRange(selectedRequest.start_time, selectedRequest.end_time) }}</span>
        </div>
        <div class="detail-row" v-if="selectedRequest.request_type !== 'single_partial_day'">
          <strong>Duration:</strong>
          <span>{{ calculateDuration(selectedRequest) }} day{{ calculateDuration(selectedRequest) > 1 ? 's' : '' }}</span>
        </div>
        <div class="detail-row">
          <strong>Status:</strong>
          <el-tag
            :type="getStatusType(selectedRequest.status)"
            size="small"
          >
            {{ capitalizeStatus(selectedRequest.status) }}
          </el-tag>
        </div>
        <div class="detail-row">
          <strong>Reason:</strong>
          <span>{{ selectedRequest.reason || 'N/A' }}</span>
        </div>
        <div v-if="selectedRequest.reviewer" class="detail-row">
          <strong>Reviewed By:</strong>
          <span>
            {{ selectedRequest.reviewer.first_name }} {{ selectedRequest.reviewer.last_name }}
            <br>
            <span class="text-muted small">{{ formatDateTime(selectedRequest.reviewed_at) }}</span>
          </span>
        </div>
        <div v-if="selectedRequest.status === 'approved' || selectedRequest.status === 'denied'" class="detail-row">
          <strong>Admin Notes:</strong>
          <span>{{ selectedRequest.admin_notes || 'N/A' }}</span>
        </div>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="showViewModal = false">Close</el-button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import { timeOffRequestsService } from 'src/serviceHandlers/timeOffRequestsService';
import { caregiversService } from 'src/serviceHandlers/caregiversService';
import TimeOffRequestModal from 'src/components/TimeOffRequestModal.vue';
import TimeOffReviewModal from 'src/components/TimeOffReviewModal.vue';
import { useLoginStore } from 'src/stores/loginStore';
import { useNotificationsStore } from 'src/stores/notificationsStore';

export default {
  name: 'TimeOffRequests',
  components: {
    TimeOffRequestModal,
    TimeOffReviewModal
  },
  data() {
    return {
      requests: [],
      caregivers: [],
      loading: false,
      loginStore: useLoginStore(),
      notificationsStore: useNotificationsStore(),

      // Filters
      selectedStatus: 'pending',
      selectedCaregiver: [],
      selectedAdmin: [],
      selectedDateRange: null,
      admins: [],

      // Modals
      showRequestModal: false,
      showReviewModal: false,
      showViewModal: false,
      selectedRequest: null,

      // Pagination
      pagination: {
        currentPage: 1,
        perPage: 20,
        total: 0,
        totalPages: 1
      },

      // Sorting
      sortField: 'start',
      sortOrder: 'asc'
    };
  },
  computed: {
    isAdmin() {
      return this.loginStore.user?.user_type === 'Admin';
    },

    userId() {
      return this.loginStore.user?.id;
    },

    tableColumns() {
      const columns = [
        {
          key: 'start',
          label: 'Start',
          minWidth: '200',
          sortable: true
        },
        {
          key: 'end',
          label: 'End',
          minWidth: '200',
          sortable: true
        },
        {
          key: 'duration',
          label: 'Duration',
          width: '100',
          sortable: false
        },
        {
          key: 'status',
          label: 'Status',
          width: '120',
          sortable: true
        },
        {
          key: 'actions',
          label: 'Actions',
          width: '200',
          fixed: 'right',
          sortable: false
        }
      ];

      // Add employee and manager columns for admins
      if (this.isAdmin) {
        columns.unshift({
          key: 'employee',
          label: 'Employee',
          minWidth: '150',
          sortable: true
        });

        // Add manager column after employee
        columns.splice(1, 0, {
          key: 'manager',
          label: 'Manager',
          minWidth: '150',
          sortable: true,
          class: 'hide-on-small'
        });

        // Add reviewed by column (after status, which is now at index 5)
        columns.splice(columns.length - 1, 0, {
          key: 'reviewed_by',
          label: 'Reviewed By',
          minWidth: '150',
          sortable: true,
          class: 'hide-on-small'
        });
      }

      return columns;
    },

    statusOptions() {
      return [
        { id: 'All', name: 'All' },
        { id: 'pending', name: 'Pending' },
        { id: 'approved', name: 'Approved' },
        { id: 'denied', name: 'Denied' }
      ];
    },

    caregiverOptions() {
      return this.caregivers.map(c => ({
        id: c.id,
        name: `${c.first_name} ${c.last_name}`
      }));
    },

    adminOptions() {
      return this.admins.map(a => ({
        id: a.id,
        name: a.name
      }));
    }
  },
  mounted() {
    this.loadRequests();
    if (this.isAdmin) {
      this.loadCaregivers();
      this.loadAdmins();
    }
  },
  methods: {
    async loadRequests(page = null) {
      this.loading = true;
      try {
        // Update current page if provided
        if (page !== null) {
          this.pagination.currentPage = page;
        }

        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.perPage
        };

        if (this.selectedStatus !== 'All') {
          params.status = this.selectedStatus;
        }

        if (this.isAdmin && this.selectedCaregiver && this.selectedCaregiver.length > 0) {
          params.caregiver_id = this.selectedCaregiver.join(',');
        }

        if (this.isAdmin && this.selectedAdmin && this.selectedAdmin.length > 0) {
          params.adminId = this.selectedAdmin.join(',');
        }

        if (this.selectedDateRange && this.selectedDateRange.length === 2) {
          params.startDate = this.selectedDateRange[0];
          params.endDate = this.selectedDateRange[1];
        }

        // Add sorting parameters
        if (this.sortField) {
          params.sortField = this.sortField;
          params.sortOrder = this.sortOrder;
        }

        const response = await timeOffRequestsService.list(params);

        // Handle new paginated response format
        if (response && response.data) {
          this.requests = response.data;
          this.pagination.total = response.pagination.total;
          this.pagination.totalPages = response.pagination.totalPages;
        } else {
          // Fallback for non-paginated response
          this.requests = response || [];
          this.pagination.total = this.requests.length;
          this.pagination.totalPages = Math.ceil(this.requests.length / this.pagination.perPage) || 1;
        }
      } catch (error) {
        // Only show error notification for actual errors (not empty results)
        if (error.response && error.response.status !== 404) {
          this.$notifyError('Error loading time off requests');
        }
        this.requests = [];
        this.pagination.total = 0;
        this.pagination.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },

    async loadCaregivers() {
      try {
        const response = await caregiversService.list();
        this.caregivers = response || [];
      } catch (error) {
        // Error loading caregivers
      }
    },

    async loadAdmins() {
      try {
        const response = await caregiversService.getAdmins();
        this.admins = (response || []).map(admin => ({
          id: Number(admin.id) || admin.id,
          name: admin.name || `${admin.first_name} ${admin.last_name}`
        }));
      } catch (error) {
        // Error loading admins
      }
    },

    openCreateModal() {
      this.selectedRequest = null;
      this.showRequestModal = true;
    },

    editRequest(request) {
      this.selectedRequest = request;
      this.showRequestModal = true;
    },

    viewRequest(request) {
      this.selectedRequest = request;
      this.showViewModal = true;
    },

    openReviewModal(request) {
      this.selectedRequest = request;
      this.showReviewModal = true;
    },

    async deleteRequest(request) {
      try {
        await this.$confirm(
          'Are you sure you want to delete this time off request?',
          'Confirm Deletion',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }
        );

        await timeOffRequestsService.delete(request.id);
        this.$notifySuccess('Time off request deleted successfully');
        this.loadRequests();
        // Refresh notifications to update sidebar count
        this.notificationsStore.loadNotifications();
      } catch (error) {
        if (error !== 'cancel') {
          this.$notifyError('Error deleting request');
        }
      }
    },

    handleRequestSaved() {
      this.loadRequests();
      // Refresh notifications to update sidebar count
      this.notificationsStore.loadNotifications();
    },

    handleRequestReviewed() {
      this.loadRequests();
      // Refresh notifications to update sidebar count
      this.notificationsStore.loadNotifications();
    },

    canEdit(request) {
      return request.status === 'pending' && request.id_users === this.userId;
    },

    canDelete(request) {
      return (request.status === 'pending' && request.id_users === this.userId) || this.isAdmin;
    },

    canReview(request) {
      // Admins can review pending requests, but not their own
      return this.isAdmin && request.status === 'pending' && request.id_users !== this.userId;
    },

    formatRequestType(type) {
      const types = {
        'single_full_day': 'Single Full Day',
        'single_partial_day': 'Partial Day',
        'multiple_full_days': 'Multiple Days'
      };
      return types[type] || type;
    },

    formatTimeRange(startTime, endTime) {
      if (!startTime || !endTime) return 'N/A';
      return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
    },

    formatTime(timeString) {
      if (!timeString) return '';
      // Convert 24-hour time to 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    },

    getStatusType(status) {
      const types = {
        'pending': 'warning',
        'approved': 'success',
        'denied': 'danger'
      };
      return types[status] || 'info';
    },

    capitalizeStatus(status) {
      if (!status) return '';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      // Parse date as local date to avoid timezone issues
      // Extract year, month, day from the date string (YYYY-MM-DD format)
      const [year, month, day] = dateString.split('T')[0].split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    },

    calculateDuration(request) {
      // Parse dates as local dates to avoid timezone issues
      const [startYear, startMonth, startDay] = request.start_date.split('T')[0].split('-');
      const [endYear, endMonth, endDay] = request.end_date.split('T')[0].split('-');
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    },

    handleFilterChange() {
      // Reset to page 1 when filters change
      this.pagination.currentPage = 1;
      this.loadRequests(1);
    },

    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.loadRequests(1);
    },

    handleSortChange({ prop, order }) {
      this.sortField = prop;
      this.sortOrder = order;
      this.pagination.currentPage = 1;
      this.loadRequests(1);
    }
  }
};
</script>

<style scoped>
.compact-filter-section {
  --primary-color: #6BA6CD;
  --primary-hover: #5A94B8;
  --background-glass: rgba(255, 255, 255, 0.08);
  --border-color: rgba(255, 255, 255, 0.12);
  --text-primary: #fff;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --shadow-subtle: 0 2px 8px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0;
}

.filter-row-compact {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: var(--background-glass);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-subtle);
  transition: all 0.2s ease;
  flex-wrap: wrap;
}

.filter-row-compact:hover {
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}

.filter-group-compact {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-width: 180px;
}

.compact-select {
  width: 100%;
}

.date-range-group {
  min-width: 500px;
  flex-grow: 1;
  max-width: 600px;
}

/* Phones: fixed 500px min-width forces the whole page wider than the
   screen — let every filter take the full row instead */
@media (max-width: 767px) {
  .filter-group-compact,
  .date-range-group {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  .compact-date-picker,
  .date-range-group >>> .el-date-editor--daterange.el-input__inner {
    width: 100% !important;
  }
}

.compact-date-picker {
  width: 100% !important;
}

/* Date Range Picker Styling */
::v-deep .el-date-editor {
  width: 100%;
}

::v-deep .el-date-editor .el-input__inner {
  background-color: #656565 !important;
  border: 1px solid #4a5568;
  color: #ffffff;
}

::v-deep .el-date-editor .el-input__inner:focus {
  border-color: #4caf50;
}

::v-deep .el-date-editor .el-input__icon {
  color: #a0aec0;
}

::v-deep .el-date-editor--daterange {
  width: 100% !important;
}

::v-deep .el-date-editor--daterange .el-input__suffix {
  right: 10px !important;
}

::v-deep .el-range-separator {
  color: #ffffff !important;
  padding: 0 5px !important;
}

::v-deep .el-range-input {
  background-color: #656565 !important;
  color: #ffffff !important;
}

::v-deep .el-range__close-icon {
  right: 10px !important;
}

::v-deep .el-date-editor--daterange .el-range__icon {
  color: #a0aec0;
}

.text-muted {
  color: #999;
}

.small {
  font-size: 12px;
}

/* Element UI style overrides for dark theme */
::v-deep .el-table {
  background-color: #2a2a2a;
  color: #d9d9d9;
}

::v-deep .el-table th {
  background-color: #1f1f1f;
  color: #d9d9d9;
}

::v-deep .el-table tr {
  background-color: #2a2a2a;
}

::v-deep .el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: #3a3a3a !important;
}

::v-deep .el-tag {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
}

/* View Details Modal */
.view-details-content {
  padding: 0.5rem 0;
}

.detail-row {
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row strong {
  flex: 0 0 140px;
  color: #6ba6cd;
  font-weight: 600;
}

.detail-row span {
  flex: 1;
  color: rgba(255, 255, 255, 0.9);
}
</style>
